import { Link } from 'react-router-dom';

import { Badge, Button } from '@/components/ui';
import { StoreCardVisual } from '@/components/store/StoreCardVisual';
import { formatCOP } from '@/lib/format';
import {
  EXPERIENCE_CATEGORY_LABELS,
  type Experience,
  type ExperienceCategory,
} from '@/hooks/useExperiences';

/**
 * StoreExperienceCard — card premium de Experiencia VIP en el tab Experiencias
 * de la Tienda (`/store`). Zona superior con bloque de icono semántico
 * (`StoreCardVisual`) por categoría/nombre — SIN fotos. Conserva las dos
 * acciones reales del store: "Detalle" (→ `/experiences/:id`) y "Agregar"
 * (add-to-cart vía `onAdd`).
 */

interface StoreExperienceCardProps {
  experience: Experience;
  onAdd: (experience: Experience) => void;
}

export function StoreExperienceCard({ experience, onAdd }: StoreExperienceCardProps): JSX.Element {
  const categoryLabel =
    EXPERIENCE_CATEGORY_LABELS[experience.category as ExperienceCategory] ?? experience.category;

  return (
    <div className="group flex h-full flex-col overflow-hidden rounded-card border border-fc-line bg-fc-paper shadow-sm transition duration-200 hover:-translate-y-1 hover:border-primary-200 hover:shadow-lg">
      <StoreCardVisual category={experience.category} name={experience.title}>
        <Badge tone="gold" className="absolute left-3 top-3 shadow-sm">
          {categoryLabel}
        </Badge>
      </StoreCardVisual>

      <div className="flex flex-1 flex-col p-4 md:p-5">
        <h3 className="line-clamp-2 font-display text-base font-semibold text-fc-ink">
          {experience.title}
        </h3>
        {experience.subtitle && (
          <p className="mt-1 line-clamp-1 text-xs text-fc-muted">{experience.subtitle}</p>
        )}
        {experience.description && (
          <p className="mt-2 line-clamp-2 text-sm text-fc-muted">{experience.description}</p>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 border-t border-fc-line/70 pt-4">
          <span className="font-display text-base font-semibold tabular-nums text-fc-ink">
            {formatCOP(experience.priceCents)}
          </span>
          <div className="flex gap-2">
            <Button as={Link} to={`/experiences/${experience.id}`} variant="secondary" size="sm">
              Detalle
            </Button>
            <Button variant="primary" size="sm" onClick={() => onAdd(experience)}>
              Agregar
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
